import { ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import searchBarIcon from "@/assets/jobs/icon-search_bar.svg";

export interface FilterOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface FilterConfig {
  placeholder: string;
  icon?: ReactNode;
  options: FilterOption[];
}

interface FilterSectionProps {
  // Search functionality
  searchTerm: string;
  onSearchChange: (term: string) => void;
  searchPlaceholder?: string;
  
  // Filter configurations
  filters: {
    [key: string]: {
      value: string;
      onChange: (value: string) => void;
      config: FilterConfig;
    };
  };
  
  // Layout options
  useCard?: boolean;
  className?: string;
  
  // Clear filters functionality
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  
  // Bulk actions (optional, for job-like functionality)
  selectedItems?: string[];
  bulkActions?: Array<{
    action: string;
    label: string;
    icon: ReactNode;
  }>;
  onBulkAction?: (action: string) => void;
}

export function FilterSection({
  searchTerm,
  onSearchChange,
  searchPlaceholder = "Search...",
  filters,
  useCard = false,
  className = "",
  hasActiveFilters = false,
  onClearFilters,
  selectedItems = [],
  bulkActions = [],
  onBulkAction
}: FilterSectionProps) {
  const filterEntries = Object.entries(filters);
  
  const content = (
    <div className="mb-6">
      {/* Mobile Layout - Stack vertically */}
      <div className="block md:hidden space-y-4">
        <div className="relative w-full">
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pr-10"
          />
          <img 
            src={searchBarIcon} 
            alt="Search" 
            className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4" 
          />
        </div>
        
        <div className="flex flex-col gap-3">
          {filterEntries.map(([key, filter]) => (
            <Select key={key} value={filter.value} onValueChange={filter.onChange}>
              <SelectTrigger className="w-full [&>svg]:text-[#FF7A00] [&>svg]:opacity-100">
                {filter.config.icon}
                <SelectValue placeholder={filter.config.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {filter.config.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          
          {hasActiveFilters && onClearFilters && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onClearFilters}
              className="text-gray-600 hover:text-gray-900 font-medium w-full"
            >
              Clear filters
            </Button>
          )}
        </div>
      </div>

      {/* Desktop Layout - Single row */}
      <div className="hidden md:flex items-center gap-4">
        <div className="relative flex-1">
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pr-10"
          />
          <img 
            src={searchBarIcon} 
            alt="Search" 
            className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4" 
          />
        </div>
        
        <div className="flex items-center gap-4">
          {filterEntries.map(([key, filter]) => (
            <Select key={key} value={filter.value} onValueChange={filter.onChange}>
              <SelectTrigger className="w-40 [&>svg]:text-[#FF7A00] [&>svg]:opacity-100">
                {filter.config.icon}
                <SelectValue placeholder={filter.config.placeholder} />
              </SelectTrigger>
              <SelectContent>
                {filter.config.options.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ))}
          
          {hasActiveFilters && onClearFilters && (
            <Button 
              variant="secondary" 
              size="sm"
              onClick={onClearFilters}
              className="text-gray-600 hover:text-gray-900 font-medium [&>svg]:text-[#FF7A00] [&>svg]:opacity-100"
            >
              Clear filters
            </Button>
          )}
          
          {selectedItems.length > 0 && bulkActions.length > 0 && onBulkAction && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600 space-grotesk">
                {selectedItems.length} selected
              </span>
              {bulkActions.map((action) => (
                <Button
                  key={action.action}
                  variant="outline"
                  size="sm"
                  onClick={() => onBulkAction(action.action)}
                  className="space-grotesk text-xs"
                >
                  {action.icon}
                  {action.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (useCard) {
    return (
      <div className={`bg-white rounded-lg border border-gray-200 p-6 ${className}`}>
        {content}
      </div>
    );
  }

  return <div className={className}>{content}</div>;
}
